import { useRef } from 'react'

import { Box, Dialog, FormHelperText, Stack } from '@mui/material'
import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { AddTerritory } from './components/AddTerritory'
import { EmptyTerritories } from './components/EmptyTerritories'
import { Header } from './components/Header'

import { useDialogState } from './hooks/useDialogState'
import { useTerritories } from './hooks/useTerritories'

export type InputValue = string[]
const defaultComponentValue: InputValue = []

const transitionDuration = { exit: 100 }

export type InputProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = InputValue,
> = {
  territoriesConfig: uui.domain.client.rm.TerritoriesDriversAndVehicles
  territoryNames: Record<string, string>
} & TidalFormBridgeOptions<InputValue, TargetField, State, AcceptedFormValue>

export function CourierTerritoryAccessField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = InputValue,
>(props: InputProps<TargetField, State, AcceptedFormValue>) {
  const {
    territoriesConfig,
    territoryNames,
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const rootRef = useRef<HTMLDivElement | null>(null)

  const {
    fieldInfo: {
      errorText,
      field: { visible, value: fieldValue },
      onChange,
    },
  } = useTidalFormBridge<InputValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const { open: dialogOpen, onOpen: onOpenDialog, onClose: onCloseDialog } = useDialogState()

  const territories = useTerritories(
    fieldValue as string[],
    onChange,
    territoriesConfig,
    territoryNames,
    onCloseDialog,
    rootRef,
  )

  const hasNoTerritories =
    Object.keys(fieldValue as uui.domain.client.rm.CourierFilter).length === 0

  // message will be set only when the value has no entry
  const invalidMessage = errorText

  if (!visible) return null

  return (
    <Stack spacing={2} ref={rootRef} data-testid="courier-filter">
      <Header onOpenDialog={onOpenDialog} disabled={!territories.canAddNewTerritory} />
      <EmptyTerritories onAddTerritory={onOpenDialog} visible={hasNoTerritories} />
      <Box hidden={!invalidMessage} paddingBottom={2}>
        <FormHelperText error>{invalidMessage}</FormHelperText>
      </Box>
      <Dialog
        open={dialogOpen}
        onClose={onCloseDialog}
        data-testid="dialog"
        transitionDuration={transitionDuration}
      >
        <AddTerritory
          onClose={onCloseDialog}
          options={territories.options}
          onAddTerritory={territories.onAddTerritory}
        />
      </Dialog>
    </Stack>
  )
}
